<template>
  <div class="hubspot-sync-container">
    <h1>HubSpot CRM Synchronization</h1>
    
    <div class="status-cards">
      <div class="status-card">
        <h3>Total Customers</h3>
        <div class="status-value">{{ customers.length }}</div>
      </div>
      <div class="status-card">
        <h3>Added to HubSpot</h3>
        <div class="status-value">{{ addedCount }}</div>
      </div>
      <div class="status-card">
        <h3>Skipped (Duplicates)</h3>
        <div class="status-value">{{ skippedCount }}</div>
      </div>
      <div class="status-card">
        <h3>Errors</h3>
        <div class="status-value">{{ errorCount }}</div>
      </div>
    </div>

    <div class="action-buttons">
      <button 
        @click="startSync" 
        :disabled="isSyncing" 
        class="sync-button"
      >
        <span v-if="!isSyncing">Sync with HubSpot</span>
        <span v-else>Syncing... {{ progress }}%</span>
      </button>
      <button 
        @click="fetchCustomers" 
        :disabled="isSyncing" 
        class="refresh-button"
      >
        Refresh Customer List
      </button>
    </div>

    <div v-if="syncMessage" :class="['sync-message', syncMessageType]">
      {{ syncMessage }}
    </div>

    <div class="sync-logs">
      <h2>Synchronization Log</h2>
      <div class="log-filter">
        <label>
          <input type="checkbox" v-model="showSuccessLogs" /> Show Success
        </label>
        <label>
          <input type="checkbox" v-model="showSkippedLogs" /> Show Skipped
        </label>
        <label>
          <input type="checkbox" v-model="showErrorLogs" /> Show Errors
        </label>
      </div>
      <div class="log-container">
        <div 
          v-for="(log, index) in filteredLogs" 
          :key="index" 
          :class="['log-entry', log.type]"
        >
          <span class="log-time">{{ formatTime(log.timestamp) }}</span>
          <span class="log-message">{{ log.message }}</span>
        </div>
        <div v-if="filteredLogs.length === 0" class="no-logs">
          No logs to display
        </div>
      </div>
    </div>

    <div class="customer-list">
      <h2>Customer List</h2>
      <div class="search-filter">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search customers..." 
          class="search-input"
        />
      </div>
      <table v-if="filteredCustomers.length > 0">
        <thead>
          <tr>
            <th>ID</th>
            <th>Company</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="customer in filteredCustomers" :key="customer.id">
            <td>{{ customer.id }}</td>
            <td>{{ customer.company }}</td>
            <td>{{ customer.email }}</td>
            <td>{{ customer.phone }}</td>
            <td>
              <span :class="['status-badge', customer.syncStatus || 'pending']">
                {{ formatStatus(customer.syncStatus) }}
              </span>
            </td>
            <td>
              <button 
                @click="syncSingleCustomer(customer)" 
                :disabled="isSyncing || customer.syncStatus === 'success'"
                class="action-button"
              >
                Sync
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="no-customers">
        No customers found
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'HubSpotSync',
  data() {
    return {
      HUBSPOT_API_KEY: this.$hubspot_api_key,
      customers: [],
      existingCustomerIds: new Map(),
      isSyncing: false,
      progress: 0,
      syncMessage: '',
      syncMessageType: 'info',
      logs: [],
      addedCount: 0,
      skippedCount: 0,
      errorCount: 0,
      showSuccessLogs: true,
      showSkippedLogs: true,
      showErrorLogs: true,
      searchQuery: '',
      hubspotApiInstance: null
    };
  },
  computed: {
    filteredLogs() {
      return this.logs.filter(log => {
        if (log.type === 'success' && !this.showSuccessLogs) return false;
        if (log.type === 'skipped' && !this.showSkippedLogs) return false;
        if (log.type === 'error' && !this.showErrorLogs) return false;
        return true;
      });
    },
    filteredCustomers() {
      if (!this.searchQuery) return this.customers;
      
      const query = this.searchQuery.toLowerCase();
      return this.customers.filter(customer => {
        return (
          customer.company?.toLowerCase().includes(query) ||
          customer.email?.toLowerCase().includes(query) ||
          customer.phone?.includes(query) ||
          customer.id?.toString().includes(query)
        );
      });
    }
  },

  async mounted() {
    
    //this.createHubspotApiInstance();

    await this.fetchCustomers();
  },
  
  methods: {
    // createHubspotApiInstance() {

    //   this.hubspotApiInstance = axios.create({
    //     baseURL: 'https://api.hubapi.com',
    //     headers: {
    //       Authorization: `Bearer ${this.HUBSPOT_API_KEY}`,
    //       'Content-Type': 'application/json',
    //     },
    //     timeout: 15000 // 15 seconds timeout
    //   });
      
    //   // Add response interceptor for error handling
    //   this.hubspotApiInstance.interceptors.response.use(
    //     response => response,
    //     error => {
    //       // Log the error details
    //       console.error('API Error:', error);
          
    //       // Extract meaningful error message
    //       const errorMessage = error.response?.data?.message || 
    //                           (error.code === 'ECONNABORTED' ? 'Request timeout' : error.message);
          
    //       return Promise.reject({
    //         ...error,
    //         message: errorMessage
    //       });
    //     }
    //   );
    // },
    
    async withRetry(fn, maxRetries = 3, initialDelay = 1000) {
      let retries = 0;
      let delay = initialDelay;
      
      while (true) {
        try {
          return await fn();
        } catch (error) {
          retries++;
          if (retries > maxRetries) {
            throw error;
          }
          this.addLog(`Retrying operation (${retries}/${maxRetries}) after error: ${error.message}`, 'info');

          await new Promise(resolve => setTimeout(resolve, delay));
          delay *= 2;
        }
      }
    },
    
    async fetchCustomers() {
      try {
        this.addLog('Fetching customers from local API...', 'info');

        var param = {
          table: "customer",
          statement: "select",
          type: "list",  
          where: "", 
          selector: "", 
          value: "",    
          short: "false",
          extension: ""
        };

        var data = {}; 

        const response = await this.$func.queryAPI(param, data);

        console.log("--- fetched customers ---:", response);

        this.customers = response.map(customer => ({
          ...customer,
          syncStatus: null
        }));

        this.addLog(`Successfully fetched ${this.customers.length} customers`, 'success');
      } catch (error) {
        this.addLog(`Error fetching customers: ${error.message}`, 'error');
        this.syncMessage = 'Failed to fetch customers';
        this.syncMessageType = 'error';
        this.errorCount++;
      }
    },

async fetchExistingCustomerIds() {
  this.existingCustomerIds = new Map();
  
  try {
    this.addLog('Fetching existing companies from HubSpot via php backend...', 'info');

    const response = await axios.get(this.$hubspot_companies_endpoint);
    
    if (response.data.success) {
      response.data.results.forEach(company => {
        if (company.customer_id) {
          this.existingCustomerIds.set(company.customer_id, company.id);
        }
      });
      this.addLog(`Found ${this.existingCustomerIds.size} existing companies in HubSpot`, 'info');
    } else {
      throw new Error(response.data.error || 'Unknown error fetching companies');
    }
    
    return this.existingCustomerIds;
  } catch (error) {
    this.addLog(`Error fetching existing companies: ${error.message}`, 'error');
    this.syncMessage = 'Failed to fetch existing companies from HubSpot';
    this.syncMessageType = 'error';
    this.errorCount++;
    return new Map();
  }
},

async syncCustomer(customer) {
  if (!customer || !customer.id) {
    this.addLog(`Skipping invalid customer record`, 'error');
    return;
  }
  
  
  const customerId = customer.id.toString();
  const existingHubspotId = this.existingCustomerIds.get(customerId);
  
  if (existingHubspotId) {
    const message = `Skipping duplicate: Customer ID ${customerId} (${customer.company}) already exists in HubSpot`;
    this.addLog(message, 'skipped');
    customer.syncStatus = 'skipped';
    this.skippedCount++;
    return;
  }
  
  try {
    // Send to PHP backend for creation
    const response = await axios.post(this.$hubspot_create_company_endpoint, {
      customer: {
        company: customer.company || 'Unknown Company',
        phone: customer.phone || '',
        email: customer.email || '',
        city: customer.city || '',
        zipcode: customer.zipcode || '',
        street: customer.street || '',
        id: customerId,
        group_main_text: customer.group_main_text || '',
        group_type_text: customer.group_type_text || ''
      }
    });
    
    if (response.data.success) {
      // Store the hubspot ID for future reference
      this.existingCustomerIds.set(customerId, response.data.hubspotId);
      
      const message = `Imported customer ${customer.company} (ID: ${customerId}) to HubSpot`;
      this.addLog(message, 'success');
      customer.syncStatus = 'success';
      this.addedCount++;
    } else {
      throw new Error(response.data.error || 'Unknown error creating company');
    }
  } catch (error) {
    const message = `Error importing ${customer.company} (ID: ${customerId}): ${error.message}`;
    this.addLog(message, 'error');
    customer.syncStatus = 'error';
    this.errorCount++;
  }
},
    async startSync() {
      if (this.isSyncing) return;
      
      this.isSyncing = true;
      this.progress = 0;
      this.syncMessage = 'Starting synchronization...';
      this.syncMessageType = 'info';
      this.addedCount = 0;
      this.skippedCount = 0;
      this.errorCount = 0;
      
      try {
        // Reset all customer sync statuses
        this.customers.forEach(customer => {
          customer.syncStatus = null;
        });
        
        await this.fetchExistingCustomerIds();
        
        const totalCustomers = this.customers.length;
        if (totalCustomers === 0) {
          this.syncMessage = 'No customers to import';
          this.syncMessageType = 'info';
          this.isSyncing = false;
          return;
        }
        
        this.addLog(`Starting import of ${totalCustomers} customers to HubSpot`, 'info');
        
    
        for (let i = 0; i < this.customers.length; i++) {
          const customer = this.customers[i];
          await this.syncCustomer(customer);
         
          this.progress = Math.round(((i + 1) / totalCustomers) * 100);

          if (i < this.customers.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
        
        this.syncMessage = `Synchronization complete. Added: ${this.addedCount}, Skipped: ${this.skippedCount}, Errors: ${this.errorCount}`;
        this.syncMessageType = this.errorCount > 0 ? 'warning' : 'success';
        this.addLog(this.syncMessage, this.syncMessageType);
      } catch (error) {
        this.syncMessage = `Synchronization failed: ${error.message}`;
        this.syncMessageType = 'error';
        this.addLog(this.syncMessage, 'error');
        this.errorCount++;
      } finally {
        this.isSyncing = false;
      }
    },
    
    async syncSingleCustomer(customer) {
      if (this.isSyncing) return;
      
      this.isSyncing = true;
      try {

        if (this.existingCustomerIds.size === 0) {
          await this.fetchExistingCustomerIds();
        }
        
        await this.syncCustomer(customer);
        
        this.syncMessage = `Finished processing customer: ${customer.company}`;
        this.syncMessageType = customer.syncStatus === 'error' ? 'error' : 'success';
      } catch (error) {
        this.syncMessage = `Error syncing ${customer.company}: ${error.message}`;
        this.syncMessageType = 'error';
        this.addLog(this.syncMessage, 'error');
        this.errorCount++;
      } finally {
        this.isSyncing = false;
      }
    },
    
    async syncCustomer(customer) {
    
      if (!customer || !customer.id) {
        this.addLog(`Skipping invalid customer record`, 'error');
        return;
      }
      
      const customerId = customer.id.toString();
      const existingHubspotId = this.existingCustomerIds.get(customerId);
      
      if (existingHubspotId) {
        const message = `Skipping duplicate: Customer ID ${customerId} (${customer.company}) already exists in HubSpot with ID: ${existingHubspotId}`;
        this.addLog(message, 'skipped');
        customer.syncStatus = 'skipped';
        this.skippedCount++;
        return;
      }
      
      try {
        const hubspotCompany = {
          properties: {
            name: customer.company || 'Unknown Company',
            phone: customer.phone || '',
            email: customer.email || '',
            city: customer.city || '',
            zip: customer.zipcode || '',
            strasse: customer.street || '',
            customer_id: customerId,
            bereits_kunde: 'ja',
            group_main: customer.group_main_text || '',
            group_type: customer.group_type_text || '',
          },
        };
        
        
        const response = await this.withRetry(async () => {
          return await this.hubspotApiInstance.post(
            '/crm/v3/objects/companies',
            hubspotCompany
          );
        });
        
       
        this.existingCustomerIds.set(customerId, response.data.id);
        
        const message = `Imported customer ${customer.company} (ID: ${customerId}) to HubSpot with ID: ${response.data.id}`;
        this.addLog(message, 'success');
        customer.syncStatus = 'success';
        this.addedCount++;
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message;
        const message = `Error importing ${customer.company} (ID: ${customerId}): ${errorMessage}`;
        this.addLog(message, 'error');
        customer.syncStatus = 'error';
        this.errorCount++;
      }
    },
    
    addLog(message, type = 'info') {
      this.logs.unshift({
        message,
        type,
        timestamp: new Date()
      });
      
     
      if (this.logs.length > 100) {
        this.logs.pop();
      }
      
      console.log(`[${type.toUpperCase()}] ${message}`);
    },
    
    formatTime(timestamp) {
      return timestamp.toLocaleTimeString();
    },
    
    formatStatus(status) {
      if (!status) return 'Pending';
      
      const statusMap = {
        'success': 'Imported',
        'skipped': 'Duplicate',
        'error': 'Failed',
      };
      
      return statusMap[status] || status;
    }
  },
};
</script>

<style scoped>
.hubspot-sync-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

h1 {
  color: #333;
  margin-bottom: 20px;
}

.status-cards {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.status-card {
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 15px;
  flex: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.status-card h3 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 14px;
  color: #666;
}

.status-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

button {
  padding: 10px 15px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sync-button {
  background-color: #ff7a59;
  color: white;
  min-width: 180px;
}

.sync-button:hover:not(:disabled) {
  background-color: #f86b47;
}

.refresh-button {
  background-color: #eaf0f6;
  color: #33475b;
}

.refresh-button:hover:not(:disabled) {
  background-color: #d9e2ec;
}

.action-button {
  background-color: #eaf0f6;
  color: #33475b;
  font-size: 12px;
  padding: 5px 10px;
}

.action-button:hover:not(:disabled) {
  background-color: #d9e2ec;
}

.sync-message {
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.sync-message.info {
  background-color: #e3f3ff;
  color: #0078bd;
}

.sync-message.success {
  background-color: #eafcef;
  color: #0b875b;
}

.sync-message.warning {
  background-color: #fff8e3;
  color: #c55a11;
}

.sync-message.error {
  background-color: #ffebe6;
  color: #de350b;
}

.sync-logs {
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
}

h2 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 18px;
}

.log-filter {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
}

.log-filter label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
}

.log-container {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  background-color: white;
}

.log-entry {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f1f1;
  font-size: 14px;
  display: flex;
}

.log-entry:last-child {
  border-bottom: none;
}

.log-entry.success {
  border-left: 3px solid #0b875b;
}

.log-entry.error {
  border-left: 3px solid #de350b;
}

.log-entry.skipped {
  border-left: 3px solid #c55a11;
}

.log-entry.info {
  border-left: 3px solid #0078bd;
}

.log-time {
  color: #666;
  margin-right: 10px;
  font-size: 12px;
  min-width: 80px;
}

.log-message {
  flex: 1;
}

.customer-list {
  background-color: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.search-filter {
  margin-bottom: 15px;
}

.search-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  font-size: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: left;
  padding: 12px 15px;
  border-bottom: 2px solid #e1e5e9;
  font-size: 14px;
  color: #5f6b7a;
}

td {
  padding: 12px 15px;
  border-bottom: 1px solid #f1f1f1;
  font-size: 14px;
}

.status-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.success {
  background-color: #eafcef;
  color: #0b875b;
}

.status-badge.error {
  background-color: #ffebe6;
  color: #de350b;
}

.status-badge.skipped {
  background-color: #fff8e3;
  color: #c55a11;
}

.status-badge.pending {
  background-color: #f4f5f7;
  color: #6b778c;
}

.no-customers, .no-logs {
  text-align: center;
  padding: 20px;
  color: #6b778c;
  font-size: 14px;
}
</style>